// Picker inspection harness (Jun 27) — DEV-only.
//
// "Generate 20" runs helpers/brand_setup_picker.pick_all_recipes_for_plan
// for a brand WITHOUT rendering, and shows the picked recipes so the curator
// can verify (a) every scene recipe is UNIQUE (a "Displayed N×" counter on
// each thumbnail — should always read 1×) and (b) fusion picks pair
// background + COMPOSITION with the right camera/lighting.
//
// Each card also has a one-off Generate button. With "Auto text overlay" on
// (default), Generate runs the FULL text-overlay pipeline (Jun 28):
//   1. render a TEXT-FREE fusion scene (flux-fusion-test, text_free=true) —
//      background + composition chunks, no baked headline.
//   2. vision-pick a text template + tone + zone (pick-text-template).
//   3. composite the chosen text-templates/*.json over the scene via
//      JsonSocialPost + mergedSceneUrl (tone-flipped for legibility).
// Toggle the overlay off to eyeball the bare text-free scene. Global Camera
// Angle / Lighting toggles + force_fusion behave as before.
//
// DEV-only: harmless in prod (picker-test + flux-fusion-test + pick-text-
// template are dev/auth-skiplist routes), but only meant for local curation.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, getAuthHeaders, suggestSocialCopy } from '@/lib/api';
import type { SocialPostSlots } from '@/lib/api';
import JsonSocialPost from '@/pages/app/components/social-designs/json-templates/JsonSocialPost';
import type { JsonTemplate, TextElement } from '@/pages/app/components/social-designs/json-templates/types';
import { PLACEHOLDER_SLOTS } from '@/lib/socialPostPipeline';
import {
  getTextTemplateList,
  getTextTemplate,
} from '@/pages/app/components/social-designs/text-templates/registry';

// ─── Text-overlay dev helpers (mirror /moodboard-previews) ────────────
/** Compact summary of where a template's TEXT blocks sit, for the vision
 *  reserve hint, e.g. 'top'. */
type ReserveZone = 'top' | 'bottom' | 'left' | 'right' | 'center';

/** Which region a template's TEXT sits in — drives the render reserve hint.
 *  'top'|'bottom'|'left'|'right' → keep that side open, product opposite.
 *  'center' → text is dead-center → tell the render to make the product a
 *  full-bleed hero (else a centered product collides with the text and the
 *  reserved margins sit empty). null → text SPANS top+bottom (frames a
 *  centered product; default centering already clears it). */
function dominantTextZone(tpl: JsonTemplate): ReserveZone | null {
  const W = tpl.canvas?.width || 1024;
  const H = tpl.canvas?.height || 1024;
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  let n = 0;
  for (const el of tpl.elements) {
    if (el.type !== 'text') continue;
    n++;
    minX = Math.min(minX, el.x / W);
    minY = Math.min(minY, el.y / H);
    maxX = Math.max(maxX, (el.x + el.width) / W);
    maxY = Math.max(maxY, (el.y + (el.height ?? 60)) / H);
  }
  if (!n) return null;
  // Text spans most of the height → top+bottom frame → centered product, no reserve.
  if (maxY - minY > 0.55) return null;
  const vc = (minY + maxY) / 2;
  if (vc < 0.42) return 'top';
  if (vc > 0.58) return 'bottom';
  const hc = (minX + maxX) / 2;
  if (hc < 0.42) return 'left';
  if (hc > 0.58) return 'right';
  return 'center'; // dead-center text → full-bleed hero render (no empty margins)
}

type ZoneColor = { r: number; g: number; b: number; lum: number };

/** Mean color + luminance of the sub-region of a base64 PNG where the text
 *  will sit, so the overlay color can be tinted to the scene. Whole image
 *  when zone is null. */
function sampleZoneColor(imageB64: string, zone: ReserveZone | null): Promise<ZoneColor> {
  const fallback: ZoneColor = { r: 128, g: 128, b: 128, lum: 0.5 };
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const N = 48;
        const c = document.createElement('canvas');
        c.width = N;
        c.height = N;
        const ctx = c.getContext('2d');
        if (!ctx) return resolve(fallback);
        ctx.drawImage(img, 0, 0, N, N);
        let x0 = 0, y0 = 0, x1 = N, y1 = N;
        if (zone === 'top') y1 = Math.round(N * 0.4);
        else if (zone === 'bottom') y0 = Math.round(N * 0.6);
        else if (zone === 'left') x1 = Math.round(N * 0.4);
        else if (zone === 'right') x0 = Math.round(N * 0.6);
        else if (zone === 'center') {
          x0 = Math.round(N * 0.25); x1 = Math.round(N * 0.75);
          y0 = Math.round(N * 0.3); y1 = Math.round(N * 0.7);
        }
        const { data } = ctx.getImageData(0, 0, N, N);
        let sr = 0, sg = 0, sb = 0, sl = 0, count = 0;
        for (let y = y0; y < y1; y++) {
          for (let x = x0; x < x1; x++) {
            const i = (y * N + x) * 4;
            const R = data[i], G = data[i + 1], B = data[i + 2];
            sr += R; sg += G; sb += B;
            sl += (0.299 * R + 0.587 * G + 0.114 * B) / 255;
            count++;
          }
        }
        if (!count) return resolve(fallback);
        resolve({ r: sr / count, g: sg / count, b: sb / count, lum: sl / count });
      } catch {
        resolve(fallback);
      }
    };
    img.onerror = () => resolve(fallback);
    img.src = `data:image/png;base64,${imageB64}`;
  });
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return { h, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Derive a legible, THEME-TINTED text color from the background it sits on:
 *  take the bg hue, push saturation up, and set lightness for contrast —
 *  bright bg → dark saturated tint; dark bg → light gentle tint. Matches the
 *  render's color theme instead of flat white/black. */
function deriveTextColor(c: ZoneColor): string {
  const { h, s } = rgbToHsl(c.r, c.g, c.b);
  if (c.lum >= 0.5) {
    // bright scene → dark, saturated, theme-hued text
    const sat = Math.min(0.85, Math.max(0.5, s * 1.5 + 0.25));
    return hslToHex(h, sat, 0.22);
  }
  // dark scene → light, gently-tinted text (keep contrast high)
  const sat = Math.min(0.4, Math.max(0.08, s * 0.6));
  return hslToHex(h, sat, 0.92);
}

/** Clone a template forcing every TEXT element to the derived color.
 *  Buttons/shapes keep their own colors (scene-independent). */
function applyTextColor(template: JsonTemplate, color: string): JsonTemplate {
  return {
    ...template,
    elements: template.elements.map((el) =>
      el.type === 'text' ? ({ ...el, color } as TextElement) : el,
    ),
  };
}

/** Composited text overlay scaled to its own measured width. The 1080² text
 *  template is drawn over the text-free scene via JsonSocialPost's
 *  mergedSceneUrl hook (scene = full-canvas bg, no product slot). */
function OverlayPreview({
  template,
  slots,
  sceneB64,
}: {
  template: JsonTemplate;
  slots: SocialPostSlots;
  sceneB64: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width ?? 0;
      if (cw) setW(cw);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const canvasW = template.canvas?.width || 1080;
  const scale = w ? w / canvasW : 0;
  return (
    <div
      ref={ref}
      className="relative w-full aspect-square overflow-hidden rounded-md border border-surface-dark/40 bg-white"
    >
      {scale > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
          }}
        >
          <JsonSocialPost
            config={template}
            slots={slots}
            productImageUrl=""
            mergedSceneUrl={`data:image/png;base64,${sceneB64}`}
          />
        </div>
      )}
    </div>
  );
}

type PickResult = { template_id: string; color: string; tone: 'light' | 'dark'; zone: string; reason: string };

/** base64 → File, so suggest-copy gets a REAL product image (its empty stub
 *  blob makes OpenAI vision 400 "invalid base64 image_url"). */
function b64ToFile(b64: string, mime: string): File {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], `product.${mime.split('/')[1] || 'png'}`, { type: mime });
}

type PickerSlot = {
  slot_idx: number;
  vibe: string | null;
  kind: string | null;
  product_name: string | null;
  product_type: string | null;
  model_relation: string | null;
  recipe_id: string | null;
  recipe_preview_url: string | null;
  synth_cycle: number;
  synth_pool_size: number;
  synth_variance: number;
  recipe_matched: boolean;
  is_fusion: boolean;
  composition_recipe_id: string | null;
  composition_preview_url: string | null;
  camera: string | null;
  lighting: string | null;
};

type PickerResponse = {
  elapsed_ms: number;
  dry_run: boolean;
  summary: {
    total_slots: number;
    matched: number;
    no_match: number;
    distinct_recipes: number;
    fusion_slots: number;
    distinct_compositions: number;
    by_vibe: Record<string, number>;
    by_kind: Record<string, number>;
    by_cycle: Record<string, number>;
  };
  slots: PickerSlot[];
  products_used: Array<{ id: string; name: string | null; category: string | null }>;
};

type RenderState = {
  image_b64?: string;       // the text-free fusion scene
  error?: string;
  pick?: PickResult;        // vision-picked text template + tone + zone
  pickError?: string;       // non-fatal — overlay falls back to bare scene
  serverBake?: string;      // Playwright-baked composite PNG (b64) for comparison
};

const DEFAULT_BRAND = '7ea31dc2-1945-4e3c-8656-22f913732ad1';

/** "vibe_slug/id" → {vibe_slug, id} for a flux-fusion-test reference. */
function splitRecipeId(id: string | null): { vibe_slug: string; id: string } | null {
  if (!id) return null;
  const i = id.indexOf('/');
  return i < 0 ? { vibe_slug: '', id } : { vibe_slug: id.slice(0, i), id: id.slice(i + 1) };
}

// Short id for display: vibe-slug + last 8 of the moodboard id.
function shortId(id: string | null): string {
  if (!id) return '—';
  const [vibe, rest] = id.includes('/') ? id.split('/') : ['', id];
  return vibe ? `${vibe}/…${rest.slice(-8)}` : `…${rest.slice(-8)}`;
}

function RecipeThumb({
  label,
  id,
  previewUrl,
  count,
}: {
  label: string;
  id: string | null;
  previewUrl: string | null;
  count: number;
}) {
  const dup = count > 1;
  return (
    <div className="flex-1 min-w-0">
      <div className="text-[9px] font-bold uppercase tracking-wide text-accent mb-0.5">{label}</div>
      <div
        className={`aspect-square rounded-md overflow-hidden border bg-white ${
          dup ? 'border-red-500 ring-2 ring-red-400' : 'border-surface-dark/40'
        }`}
      >
        {previewUrl ? (
          <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[9px] text-dark-muted text-center px-1">
            {id ? 'no preview' : '—'}
          </div>
        )}
      </div>
      <div className="text-[9px] text-dark-muted truncate mt-0.5" title={id ?? ''}>
        {shortId(id)}
      </div>
      <div className={`text-[9px] font-semibold ${dup ? 'text-red-600' : 'text-emerald-700'}`}>
        Displayed {count}×
      </div>
    </div>
  );
}

export default function PickerTestPage() {
  const [brandId, setBrandId] = useState(DEFAULT_BRAND);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PickerResponse | null>(null);

  // Reset-variance
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState<string | null>(null);

  // Per-card render (Generate)
  const [productPreview, setProductPreview] = useState<string | null>(null);
  const [productB64, setProductB64] = useState<string | null>(null);
  const [productMime, setProductMime] = useState('image/png');
  const [productName, setProductName] = useState('');
  const [cameraOn, setCameraOn] = useState(true);
  const [lightingOn, setLightingOn] = useState(true);
  // force_fusion ON = preview the background+composition pairing on every
  // eligible slot regardless of cycle. OFF (default) = EXACT production:
  // sends force_fusion=null → picker defers to the BRAND_SETUP_FUSION env
  // gate, so fusion fires only at cycle≥2 just like a real batch.
  const [forceFusion, setForceFusion] = useState(false);
  // commitPicks ON = dry_run OFF → the picker calls record_pick, advancing
  // rotation exactly like a real batch (no render, no credits). Lets you
  // run twice and confirm batch 2 picks fresh/rotated recipes. Default OFF
  // so plain inspection never dirties the brand's rotation history.
  const [commitPicks, setCommitPicks] = useState(false);
  const [rendering, setRendering] = useState<Record<number, boolean>>({});
  const [renders, setRenders] = useState<Record<number, RenderState>>({});

  // Text-overlay pipeline (Jun 28 — template-first). overlayOn = assign a text
  // template up front (rotation, for variety) → render a text-free scene that
  // RESERVES that template's text zone → tone from the zone's luminance →
  // composite. No vision pick: the render adapts to the chosen template, so
  // variety comes from rotation (not "best-fit on a centered product").
  const [overlayOn, setOverlayOn] = useState(true);
  const [ovHeadline, setOvHeadline] = useState('Glow that lasts');
  const [ovSubhead, setOvSubhead] = useState('Clinically tested daily radiance');
  const [ovCta, setOvCta] = useState('Shop now');
  const [suggesting, setSuggesting] = useState(false);
  const [baking, setBaking] = useState<Record<number, boolean>>({});
  const textTemplates = useMemo(() => getTextTemplateList(), []);
  const overlaySlots: SocialPostSlots = useMemo(
    () => ({
      ...PLACEHOLDER_SLOTS,
      headline: ovHeadline,
      subhead: ovSubhead,
      cta: ovCta,
      product_name: productName.trim() || PLACEHOLDER_SLOTS.product_name,
    }),
    [ovHeadline, ovSubhead, ovCta, productName],
  );

  // Lightbox — click any render to open it large (composite included).
  const [lightbox, setLightbox] = useState<{ sceneB64: string; template: JsonTemplate | null } | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  // Generate product-aware overlay copy via the free suggest-copy endpoint
  // (the same one production uses). Fills the headline/subhead/cta inputs so
  // the overlay shows real product copy instead of the static placeholder.
  const handleSuggestCopy = useCallback(async () => {
    if (!productB64) {
      setError('Upload a product image (top bar) before suggesting copy.');
      return;
    }
    setSuggesting(true);
    setError(null);
    try {
      const { slots } = await suggestSocialCopy({
        productName: productName.trim() || 'the product',
        productDescription: '',
        productFile: b64ToFile(productB64, productMime), // real image — stub blob 400s
      });
      if (slots.headline) setOvHeadline(slots.headline);
      if (slots.subhead) setOvSubhead(slots.subhead);
      if (slots.cta) setOvCta(slots.cta);
    } catch (e) {
      setError(`Suggest copy failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSuggesting(false);
    }
  }, [productB64, productMime, productName]);

  // Server bake (Playwright) — renders the SAME template (color-applied) over
  // the scene headless → PNG, to compare against the client OverlayPreview.
  // This is the production bake path; here it's a per-card proof.
  const handleBakeServer = useCallback(
    async (slotIdx: number, sceneB64: string, pick: PickResult) => {
      const base = getTextTemplate(pick.template_id);
      if (!base) return;
      const template = applyTextColor(base, pick.color);
      setBaking((p) => ({ ...p, [slotIdx]: true }));
      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        const resp = await fetch(`${API_BASE}/api/dev/bake-overlay`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ template, slots: overlaySlots, scene_b64: sceneB64 }),
        });
        if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
        const data = (await resp.json()) as { image_b64: string };
        setRenders((p) => ({ ...p, [slotIdx]: { ...(p[slotIdx] || {}), serverBake: data.image_b64 } }));
      } catch (e) {
        setRenders((p) => ({
          ...p,
          [slotIdx]: { ...(p[slotIdx] || {}), pickError: `bake: ${e instanceof Error ? e.message : String(e)}` },
        }));
      } finally {
        setBaking((p) => ({ ...p, [slotIdx]: false }));
      }
    },
    [overlaySlots],
  );

  const run = useCallback(async () => {
    if (!brandId.trim()) {
      setError('Enter a brand_id.');
      return;
    }
    setError(null);
    setRunning(true);
    setResult(null);
    setRenders({});
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/picker-test`, {
        method: 'POST',
        headers,
        // force_fusion is 3-state on the backend: true = force on every slot;
        // null = DEFER TO ENV = exact production (fusion only at cycle≥2 when
        // BRAND_SETUP_FUSION is on); false = fusion fully off. The "Force
        // fusion" checkbox maps OFF → null (mimic production), NOT false.
        body: JSON.stringify({
          brand_id: brandId.trim(),
          dry_run: !commitPicks,
          force_fusion: forceFusion ? true : null,
        }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 300)}`);
      setResult((await resp.json()) as PickerResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [brandId, forceFusion, commitPicks]);

  const handleReset = useCallback(async () => {
    if (!brandId.trim()) return;
    setResetting(true);
    setResetMsg(null);
    try {
      const headers = await getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const resp = await fetch(`${API_BASE}/api/dev/reset-brand-variance`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ brand_id: brandId.trim() }),
      });
      if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
      const data = (await resp.json()) as { deleted: number };
      setResetMsg(`Reset — cleared ${data.deleted} rotation rows. Re-run Generate 20 for fresh picks.`);
    } catch (e) {
      setResetMsg(`Reset failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setResetting(false);
    }
  }, [brandId]);

  const onProductFile = useCallback((file: File | null) => {
    if (!file) return;
    setProductMime(file.type || 'image/png');
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setProductPreview(dataUrl);
      setProductB64(dataUrl.split(',', 2)[1] ?? '');
    };
    reader.readAsDataURL(file);
  }, []);

  const handleGenerate = useCallback(
    async (slot: PickerSlot) => {
      if (!productB64) {
        setRenders((p) => ({ ...p, [slot.slot_idx]: { error: 'Upload a product image first (top bar).' } }));
        return;
      }
      const refs: Array<{ vibe_slug: string; id: string; role: string }> = [];
      const bg = splitRecipeId(slot.recipe_id);
      if (bg) refs.push({ ...bg, role: 'background' });
      const comp = splitRecipeId(slot.composition_recipe_id);
      if (comp) refs.push({ ...comp, role: 'composition' });
      if (refs.length === 0) {
        setRenders((p) => ({ ...p, [slot.slot_idx]: { error: 'No recipe to render.' } }));
        return;
      }
      setRendering((p) => ({ ...p, [slot.slot_idx]: true }));
      setRenders((p) => ({ ...p, [slot.slot_idx]: {} }));

      // Template-FIRST: assign a text template up front (rotation across slots
      // → variety, no centering trap) and read its reserve zone. The render is
      // then steered to leave that zone open. No vision pick — the scene adapts
      // to the chosen template.
      let chosen: { id: string } | null = null;
      let zone: ReserveZone | null = null;
      if (overlayOn && textTemplates.length) {
        chosen = textTemplates[slot.slot_idx % textTemplates.length];
        const full = chosen ? getTextTemplate(chosen.id) : undefined;
        zone = full ? dominantTextZone(full) : null;
      }

      try {
        const headers = await getAuthHeaders();
        headers['Content-Type'] = 'application/json';
        // TEXT-FREE scene that RESERVES the chosen template's text zone (soft
        // hint — minor overlap OK). recipe-chunks = OpenAI background +
        // composition + camera + lighting (the "flux-fusion-test" name is
        // legacy; nothing FLUX runs).
        const resp = await fetch(`${API_BASE}/api/dev/flux-fusion-test`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            recipe_ids: refs,
            product_image_b64: productB64,
            product_mime: productMime,
            product_name: productName.trim() || 'the product',
            provider: 'openai',
            model: 'low',
            mode: 'recipe-chunks',
            aspect_ratio: '1:1',
            camera: cameraOn && slot.camera ? slot.camera : 'auto',
            lighting: lightingOn && slot.lighting ? slot.lighting : 'auto',
            headline: '',
            text_free: true,
            reserve_zone: zone, // null when text spans top+bottom (centered product)
          }),
        });
        if (!resp.ok) throw new Error(`${resp.status}: ${(await resp.text()).slice(0, 200)}`);
        const data = (await resp.json()) as { image_b64: string };
        const sceneB64 = data.image_b64;
        setRenders((p) => ({ ...p, [slot.slot_idx]: { image_b64: sceneB64 } }));

        // Overlay the assigned template; tone from the RESERVED zone's
        // luminance (dark area → white text, bright → near-black).
        if (overlayOn && chosen) {
          try {
            const zc = await sampleZoneColor(sceneB64, zone);
            const color = deriveTextColor(zc);
            const tone: 'light' | 'dark' = zc.lum < 0.5 ? 'light' : 'dark';
            const pick: PickResult = {
              template_id: chosen.id,
              color,
              tone,
              zone: zone ?? 'frame', // null = text frames top+bottom (centered product)
              reason: `template-first · ${zone ?? 'whole'} avg lum ${zc.lum.toFixed(2)} → ${color}`,
            };
            setRenders((p) => ({ ...p, [slot.slot_idx]: { image_b64: sceneB64, pick } }));
          } catch (pe) {
            setRenders((p) => ({
              ...p,
              [slot.slot_idx]: { image_b64: sceneB64, pickError: pe instanceof Error ? pe.message : String(pe) },
            }));
          }
        }
      } catch (e) {
        setRenders((p) => ({ ...p, [slot.slot_idx]: { error: e instanceof Error ? e.message : String(e) } }));
      } finally {
        setRendering((p) => ({ ...p, [slot.slot_idx]: false }));
      }
    },
    [productB64, productMime, productName, cameraOn, lightingOn, overlayOn, textTemplates],
  );

  // Occurrence counts → the "Displayed N×" counters + dup highlighting.
  const { sceneCounts, compCounts } = useMemo(() => {
    const scene: Record<string, number> = {};
    const comp: Record<string, number> = {};
    for (const s of result?.slots ?? []) {
      if (s.recipe_id) scene[s.recipe_id] = (scene[s.recipe_id] ?? 0) + 1;
      if (s.composition_recipe_id)
        comp[s.composition_recipe_id] = (comp[s.composition_recipe_id] ?? 0) + 1;
    }
    return { sceneCounts: scene, compCounts: comp };
  }, [result]);

  const sceneAllUnique =
    result != null && result.summary.distinct_recipes === result.summary.matched;
  const compAllUnique =
    result != null && result.summary.distinct_compositions === result.summary.fusion_slots;

  return (
    <div className="min-h-screen bg-surface text-dark">
      <header className="px-5 py-4 border-b border-surface-dark/40 bg-white sticky top-0 z-30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold">Picker test</h1>
            <p className="text-xs text-dark-muted">
              Runs the brand-setup picker (no render). Verifies unique scene recipes +
              background+composition fusion pairs. ·{' '}
              <Link to="/moodboard-previews" className="text-accent underline">
                fusion render test →
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              placeholder="brand_id (uuid)"
              spellCheck={false}
              className="border border-surface-dark/40 rounded px-2 py-1.5 text-xs font-mono w-[330px] bg-white"
            />
            <button
              onClick={() => void run()}
              disabled={running}
              className="bg-accent text-white text-sm font-semibold px-4 py-1.5 rounded disabled:opacity-50"
            >
              {running ? 'Picking…' : 'Generate 20'}
            </button>
            <button
              onClick={() => void handleReset()}
              disabled={resetting}
              title="Clear this brand's rotation history → every recipe becomes cycle 1 (fresh)"
              className="border border-red-400 text-red-600 text-sm font-semibold px-3 py-1.5 rounded disabled:opacity-50 hover:bg-red-50"
            >
              {resetting ? 'Resetting…' : 'Reset variance'}
            </button>
          </div>
        </div>

        {/* Render controls — product + camera/lighting toggles for per-card Generate */}
        <div className="flex items-center gap-3 mt-3 flex-wrap text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <span className="text-dark-muted">Product:</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => onProductFile(e.target.files?.[0] ?? null)}
              className="text-[11px] max-w-[180px]"
            />
          </label>
          {productPreview && (
            <img src={productPreview} alt="product" className="w-8 h-8 rounded object-contain border border-surface-dark/40 bg-white" />
          )}
          <input
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="product name (optional)"
            className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white w-[180px]"
          />
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={cameraOn} onChange={(e) => setCameraOn(e.target.checked)} />
            📷 Camera angle
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={lightingOn} onChange={(e) => setLightingOn(e.target.checked)} />
            💡 Lighting
          </label>
          <span className="text-dark-muted/70">
            (off = let the model choose; on = use the slot's picked value)
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-primary">
            <input type="checkbox" checked={commitPicks} onChange={(e) => setCommitPicks(e.target.checked)} />
            Commit picks
            <span className="font-normal text-dark-muted/70">
              (advances rotation like a real batch — run twice to verify freshness)
            </span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-accent ml-auto">
            <input type="checkbox" checked={forceFusion} onChange={(e) => setForceFusion(e.target.checked)} />
            Force fusion
            <span className="font-normal text-dark-muted/70">
              (off = exact production: fusion only at cycle≥2 via BRAND_SETUP_FUSION env)
            </span>
          </label>
        </div>

        {/* Text-overlay controls — Generate assigns a template (rotation),
            renders a text-free scene that RESERVES that template's zone, then
            composites it on top (tone from the zone's luminance). Copy below
            fills the overlay. Turn off to inspect the bare text-free scene. */}
        <div className="flex items-center gap-3 mt-2 flex-wrap text-xs">
          <label className="flex items-center gap-1.5 cursor-pointer font-semibold text-accent">
            <input type="checkbox" checked={overlayOn} onChange={(e) => setOverlayOn(e.target.checked)} />
            ✨ Auto text overlay
            <span className="font-normal text-dark-muted/70">
              (rotated template → reserved zone → composited)
            </span>
          </label>
          <input
            value={ovHeadline}
            onChange={(e) => setOvHeadline(e.target.value)}
            placeholder="headline"
            disabled={!overlayOn}
            className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white w-[170px] disabled:opacity-40"
          />
          <input
            value={ovSubhead}
            onChange={(e) => setOvSubhead(e.target.value)}
            placeholder="subhead"
            disabled={!overlayOn}
            className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white w-[200px] disabled:opacity-40"
          />
          <input
            value={ovCta}
            onChange={(e) => setOvCta(e.target.value)}
            placeholder="cta"
            disabled={!overlayOn}
            className="border border-surface-dark/40 rounded px-2 py-1 text-[11px] bg-white w-[120px] disabled:opacity-40"
          />
          <button
            onClick={() => void handleSuggestCopy()}
            disabled={!overlayOn || suggesting}
            title="Generate product-aware headline/subhead/cta from the product name (free)"
            className="border border-accent text-accent text-[11px] font-semibold px-2.5 py-1 rounded disabled:opacity-40 hover:bg-accent/5"
          >
            {suggesting ? 'Suggesting…' : '✨ Suggest copy'}
          </button>
          <span className="text-dark-muted/70">{textTemplates.length} templates (rotated per slot)</span>
        </div>

        {error && <p className="text-xs text-red-600 mt-2 break-words">{error}</p>}
        {resetMsg && <p className="text-xs text-emerald-700 mt-2 break-words">{resetMsg}</p>}
      </header>

      {result && (
        <div className="px-5 py-3 border-b border-surface-dark/40 bg-white">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span>
              <b>{result.summary.matched}</b>/{result.summary.total_slots} matched
              {result.summary.no_match > 0 && (
                <span className="text-red-600"> · {result.summary.no_match} NO MATCH</span>
              )}
            </span>
            <span className={sceneAllUnique ? 'text-emerald-700' : 'text-red-600 font-semibold'}>
              scene recipes: {result.summary.distinct_recipes} distinct of {result.summary.matched}{' '}
              {sceneAllUnique ? '✓ all unique' : '✗ DUPLICATES'}
            </span>
            <span className={compAllUnique ? 'text-emerald-700' : 'text-red-600 font-semibold'}>
              composition: {result.summary.distinct_compositions} distinct of{' '}
              {result.summary.fusion_slots} fusion{' '}
              {compAllUnique ? '✓ all unique' : '✗ DUPLICATES'}
            </span>
            <span className="text-dark-muted">
              cycles: {Object.entries(result.summary.by_cycle).map(([c, n]) => `${n}×c${c}`).join(' ')}
            </span>
            <span className={result.dry_run ? 'text-dark-muted' : 'text-primary font-semibold'}>
              {result.elapsed_ms}ms · {result.dry_run ? 'dry-run' : 'COMMITTED (rotation advanced)'}
            </span>
          </div>
          <div className="text-[11px] text-dark-muted mt-1">
            products: {result.products_used.map((p) => `${p.name ?? '?'} (${p.category ?? 'untyped'})`).join(', ')}
          </div>
        </div>
      )}

      <main className="px-5 py-4">
        {!result && !running && (
          <p className="text-sm text-dark-muted">
            Enter a brand_id and hit Generate 20. Hit Reset variance first to test unique picks
            from a clean slate.
          </p>
        )}
        {result && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {result.slots.map((s) => {
              const sceneCount = s.recipe_id ? sceneCounts[s.recipe_id] ?? 1 : 1;
              const compCount = s.composition_recipe_id ? compCounts[s.composition_recipe_id] ?? 1 : 1;
              const r = renders[s.slot_idx];
              const isRendering = !!rendering[s.slot_idx];
              // Theme-tinted text template for the overlay (when one was assigned).
              const pickedTpl =
                overlayOn && r?.image_b64 && r?.pick
                  ? (() => {
                      const base = getTextTemplate(r.pick.template_id);
                      return base ? applyTextColor(base, r.pick.color) : null;
                    })()
                  : null;
              const pickName = r?.pick
                ? getTextTemplate(r.pick.template_id)?.name ?? r.pick.template_id
                : null;
              return (
                <div
                  key={s.slot_idx}
                  className="rounded-lg border border-surface-dark/40 bg-white p-2 space-y-1.5"
                >
                  <div className="flex items-center justify-between text-[11px] gap-1 flex-wrap">
                    <span className="font-bold">#{s.slot_idx + 1}</span>
                    <span className="flex items-center gap-1 flex-wrap justify-end">
                      <span className="bg-surface-dark/30 px-1 py-0.5 rounded text-[9px]">{s.vibe}</span>
                      <span className="bg-surface-dark/30 px-1 py-0.5 rounded text-[9px]">{s.kind}</span>
                      <span
                        className={`px-1 py-0.5 rounded text-[9px] font-semibold ${
                          s.is_fusion ? 'bg-accent/15 text-accent' : 'bg-surface-dark/30 text-dark-muted'
                        }`}
                      >
                        c{s.synth_cycle}
                      </span>
                    </span>
                  </div>

                  <div className="text-[10px] text-dark-muted truncate" title={s.product_name ?? ''}>
                    {s.product_name ?? '—'}{' '}
                    <span className="text-dark-muted/60">({s.product_type ?? 'untyped'} · pool {s.synth_pool_size})</span>
                  </div>

                  {!s.recipe_matched ? (
                    <div className="aspect-square rounded-md border border-dashed border-red-400 flex items-center justify-center text-xs text-red-600">
                      NO MATCH
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <RecipeThumb
                        label="① Background"
                        id={s.recipe_id}
                        previewUrl={s.recipe_preview_url}
                        count={sceneCount}
                      />
                      {s.is_fusion ? (
                        <RecipeThumb
                          label="② Composition"
                          id={s.composition_recipe_id}
                          previewUrl={s.composition_preview_url}
                          count={compCount}
                        />
                      ) : (
                        // Empty half so the single thumbnail is 50% width
                        // (matches fusion-card thumb size — denser grid).
                        <div className="flex-1" />
                      )}
                    </div>
                  )}

                  {s.is_fusion && (
                    <div className="flex gap-1 text-[9px] flex-wrap">
                      <span className="bg-amber-50 text-amber-800 px-1 py-0.5 rounded">📷 {s.camera}</span>
                      <span className="bg-sky-50 text-sky-800 px-1 py-0.5 rounded">💡 {s.lighting}</span>
                    </div>
                  )}

                  {s.recipe_matched && (
                    <button
                      onClick={() => void handleGenerate(s)}
                      disabled={isRendering}
                      className="w-full bg-primary text-white text-[11px] font-semibold py-1 rounded disabled:opacity-50"
                    >
                      {isRendering ? 'Rendering…' : 'Generate'}
                    </button>
                  )}

                  {r?.image_b64 && (
                    <button
                      type="button"
                      onClick={() =>
                        setLightbox({ sceneB64: r.image_b64 as string, template: pickedTpl })
                      }
                      title="Click to open larger"
                      className="block w-full cursor-zoom-in"
                    >
                      {pickedTpl ? (
                        <OverlayPreview template={pickedTpl} slots={overlaySlots} sceneB64={r.image_b64} />
                      ) : (
                        <img
                          src={`data:image/png;base64,${r.image_b64}`}
                          alt="render"
                          className="w-full rounded-md border border-surface-dark/40"
                        />
                      )}
                    </button>
                  )}
                  {r?.pick && (
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-dark truncate" title={r.pick.template_id}>
                        {pickName}
                      </p>
                      <p className="text-[9px] text-dark-muted break-words flex items-center gap-1" title={r.pick.reason}>
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-sm border border-surface-dark/40 flex-shrink-0"
                          style={{ background: r.pick.color }}
                        />
                        {r.pick.color} · {r.pick.tone} · zone {r.pick.zone}
                      </p>
                      <button
                        onClick={() => void handleBakeServer(s.slot_idx, r.image_b64 as string, r.pick as PickResult)}
                        disabled={!!baking[s.slot_idx]}
                        title="Bake the composite server-side via Playwright (production path)"
                        className="w-full border border-emerald-500 text-emerald-700 text-[10px] font-semibold py-0.5 rounded disabled:opacity-50 hover:bg-emerald-50"
                      >
                        {baking[s.slot_idx] ? 'Baking…' : '🖨 Bake (server)'}
                      </button>
                      {r.serverBake && (
                        <>
                          <p className="text-[9px] text-emerald-700 font-semibold mt-0.5">server bake ↓</p>
                          <img
                            src={`data:image/png;base64,${r.serverBake}`}
                            alt="server bake"
                            className="w-full rounded-md border border-emerald-400"
                          />
                        </>
                      )}
                    </div>
                  )}
                  {r?.pickError && (
                    <p className="text-[9px] text-amber-700 break-words">overlay pick failed: {r.pickError}</p>
                  )}
                  {r?.error && <p className="text-[9px] text-red-600 break-words">{r.error}</p>}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <div
            className="relative"
            style={{ width: 'min(92vw, 92vh)', height: 'min(92vw, 92vh)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightbox(null)}
              title="Close (Esc)"
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-white text-dark text-lg font-bold shadow"
            >
              ×
            </button>
            {lightbox.template ? (
              <OverlayPreview
                template={lightbox.template}
                slots={overlaySlots}
                sceneB64={lightbox.sceneB64}
              />
            ) : (
              <img
                src={`data:image/png;base64,${lightbox.sceneB64}`}
                alt="render"
                className="w-full h-full object-contain rounded-md"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
